import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { useCan } from "../authz/useCan";
import ErrorPage from "../components/ErrorPage";
import AdminNotificationSubscribers from "../features/notifications/AdminNotificationSubscribers";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import { FaEnvelopeOpenText, FaPaperPlane } from "react-icons/fa";

type NotificationTopicConfig = {
  id: string;
  labelKey: string;
  descriptionKey: string;
  defaultLabel: string;
  defaultDescription: string;
};

type TestMailResult = {
  request: {
    method: string;
    url: string;
  };
  timestamp: string;
  response: Record<string, any>;
  success: boolean;
  httpStatus?: number | null;
  errorMessage?: string;
};

const NotificationSubscribers = AdminNotificationSubscribers as any;

const AdminNotifications = () => {
  const { t } = useTranslation();
  const canManageNotifications = useCan("admin.notifications", "manage");
  const canSendNotifications = useCan("admin.notifications", "send");
  const canAccess = canManageNotifications || canSendNotifications;
  const canSendTest = canSendNotifications;
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<TestMailResult | null>(null);
  const [activeTab, setActiveTab] = useState("admins");

  const adminTopics = useMemo<NotificationTopicConfig[]>(() => [
    {
      id: "APPLICATION_NEW",
      labelKey: "notifications.admin.topics.APPLICATION_NEW.label",
      descriptionKey: "notifications.admin.topics.APPLICATION_NEW.description",
      defaultLabel: "Nieuwe aanmelding ingediend",
      defaultDescription: "Abonnees ontvangen een e-mail wanneer er een nieuwe aanmelding is ingediend.",
    },
    {
      id: "MUTATION_REQUEST",
      labelKey: "notifications.admin.topics.MUTATION_REQUEST.label",
      descriptionKey: "notifications.admin.topics.MUTATION_REQUEST.description",
      defaultLabel: "Nieuwe mutatieaanvraag ingediend",
      defaultDescription: "Abonnees ontvangen een e-mail wanneer er een nieuwe mutatieaanvraag is ingediend.",
    },
    {
      id: "FAMILY_NEW",
      labelKey: "notifications.admin.topics.FAMILY_NEW.label",
      descriptionKey: "notifications.admin.topics.FAMILY_NEW.description",
      defaultLabel: "New family registered",
      defaultDescription: "Subscribers receive an email when a new family is added, for example to prepare a welcome package.",
    },
  ], []);

  const parentTopics = useMemo<NotificationTopicConfig[]>(() => [
    {
      id: "PARENT_ACTION",
      labelKey: "notifications.admin.topics.PARENT_ACTION.label",
      descriptionKey: "notifications.admin.topics.PARENT_ACTION.description",
      defaultLabel: "Ouder-updates",
      defaultDescription: "Abonnees ontvangen een e-mail wanneer er nieuwe ouder-acties zijn.",
    },
  ], []);

  const handleSendTestMail = async () => {
    if (!canSendTest) return;
    setIsSending(true);
    setResult(null);
    const baseResult = {
      request: {
        method: "POST",
        url: "/admin/notifications/test",
      },
      timestamp: new Date().toISOString(),
    };

    try {
      const { data } = await http.post("/admin/notifications/test");
      setResult({
        ...baseResult,
        response: data ?? {},
        success: Boolean(data?.success),
      });
    } catch (err: any) {
      const mapped = mapHttpErrorToMessage(err, t);
      const payload = err?.response?.data;
      setResult({
        ...baseResult,
        response:
          payload && typeof payload === "object"
            ? payload
            : {
                success: false,
                message:
                  mapped?.message ||
                  t("admin.notifications.test_failed", {
                    defaultValue: "Verzenden van de testmail is mislukt.",
                  }),
              },
        success: Boolean(payload?.success),
        httpStatus: err?.response?.status ?? null,
        errorMessage: mapped?.message,
      });
    } finally {
      setIsSending(false);
    }
  };

  const renderConsoleBlock = () => {
    if (!result) return null;

    const { request, response, success, httpStatus, timestamp, errorMessage } = result;
    const variant = success
      ? "border-emerald-300/70 bg-emerald-50/80 text-emerald-900 dark:border-emerald-500/50 dark:bg-emerald-900/30 dark:text-emerald-100"
      : response?.success === false || errorMessage
      ? "border-red-300/70 bg-red-50/80 text-red-800 dark:border-red-500/50 dark:bg-red-900/30 dark:text-red-100"
      : "border-amber-300/70 bg-amber-50/80 text-amber-800 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-100";

    const lines: string[] = [];
    if (typeof response?.success !== "undefined") {
      lines.push(`success: ${response.success ? "true" : "false"}`);
    }
    if (typeof response?.message === "string" && response.message.trim()) {
      lines.push(`message: ${response.message.trim()}`);
    }
    const remainingKeys = response
      ? Object.keys(response).filter((key) => key !== "success" && key !== "message")
      : [];
    if (remainingKeys.length > 0) {
      lines.push("");
      lines.push("details:");
      remainingKeys.forEach((key) => {
        lines.push(`  ${key}: ${JSON.stringify(response[key])}`);
      });
    }
    const consoleText = lines.length > 0 ? lines.join("\n") : JSON.stringify(response, null, 2);

    return (
      <div className="mt-4 rounded-2xl border border-indigo-100/60 bg-white/85 shadow-[0_15px_35px_rgba(15,23,42,0.1)] dark:border-indigo-500/30 dark:bg-gray-900/60">
        <div className="px-4 py-3 border-b border-indigo-50/80 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400 flex flex-wrap items-center gap-2">
          <span>{timestamp}</span>
          <span className="text-gray-400">•</span>
          <span className="text-indigo-600 font-semibold dark:text-indigo-300">{request.method}</span>
          <span className="truncate">{request.url}</span>
          {typeof httpStatus === "number" && (
            <>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600 dark:text-gray-300">HTTP {httpStatus}</span>
            </>
          )}
        </div>
        <div className={`px-4 py-3 font-mono text-sm whitespace-pre-wrap rounded-b-2xl ${variant}`}>
          <pre className="whitespace-pre-wrap break-words">{consoleText}</pre>
        </div>
      </div>
    );
  };

  if (!canAccess) {
    return (
      <ErrorPage
        status={403}
        message={t("error.403.message", {
          defaultValue: "You do not have access to this page.",
        })}
      />
    );
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaEnvelopeOpenText}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={t("admin.notifications.title", { defaultValue: "Notificaties" })}
        description={t("admin.notifications.description", {
          defaultValue: "Beheer e-mailnotificaties en verstuur testmails.",
        })}
      />

      <div className="space-y-6 text-slate-950 dark:text-white">
        <div className="lds-overview-card-enter overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t("admin.notifications.test_heading", { defaultValue: "Verzend een testmail" })}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {t("admin.notifications.test_description", {
                defaultValue:
                  "Stuur een testnotificatie naar het beheeradres om de e-mailinstellingen te controleren.",
              })}
            </p>
          </div>

          <button
            type="button"
            onClick={handleSendTestMail}
            disabled={isSending || !canSendTest}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
          >
              <FaPaperPlane aria-hidden="true" />
            {isSending
              ? t("admin.notifications.test_sending", { defaultValue: "Bezig met verzenden..." })
              : t("admin.notifications.test_button", { defaultValue: "Verzend testmail" })}
          </button>
          </div>

          {renderConsoleBlock()}
        </div>

        <div className="lds-overview-card-enter overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
          <div className="mb-5 flex flex-wrap gap-8 border-b border-slate-200 dark:border-white/10">
            <button
              type="button"
              onClick={() => setActiveTab("admins")}
              className={`relative px-3 py-4 text-sm font-semibold transition ${
                activeTab === "admins"
                  ? "text-indigo-700 dark:text-indigo-200"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              }`}
            >
              {t("admin.notifications.tabs.admins", { defaultValue: "Beheerders" })}
              {activeTab === "admins" && (
                <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-300" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("parents")}
              className={`relative px-3 py-4 text-sm font-semibold transition ${
                activeTab === "parents"
                  ? "text-indigo-700 dark:text-indigo-200"
                  : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              }`}
            >
              {t("admin.notifications.tabs.parents", { defaultValue: "Ouders" })}
              {activeTab === "parents" && (
                <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-300" />
              )}
            </button>
          </div>

          {activeTab === "admins" ? (
            <NotificationSubscribers
              key="admins"
              canManage={canManageNotifications}
              topicsConfig={adminTopics}
            />
          ) : (
            <NotificationSubscribers
              key="parents"
              canManage={canManageNotifications}
              topicsConfig={parentTopics}
            />
          )}
        </div>
      </div>
    </LdsOverviewPage>
  );
};

export default AdminNotifications;
